import { useEffect, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Cloud,
  CloudOff,
  HardDriveDownload,
  PlayCircle,
  RotateCw,
  Trash2,
  X,
} from 'lucide-react';
import type { BackupStatusResponse } from '@dragonhq/protocol';
import { api } from '../lib/api';
import { useVault } from '../../../core/lib/vault';
import { useCanAdminister } from '../lib/usePermissions';
import { generateKekWrappedKeypair, sealToPublicKey, toB64 } from '../../../core/crypto';
import { KekOnceCard } from '../lib/KekOnceCard';

/**
 * Admin-only UI for the scheduled Dropbox backup feature.
 *
 * State machine (priority order):
 *   1. Not enrolled → "Set up scheduled backup" CTA.
 *   2. Enrolled, server reports `disabled_misconfigured` → red banner + Revoke.
 *   3. Enrolled, `disabled_no_kek` → amber banner ("paste KEK + restart").
 *   4. Enrolled + ready, Dropbox not connected → "Connect Dropbox".
 *   5. Enrolled + ready + connected → status panel (Run now / Disconnect / Revoke).
 *
 * Note: this modal exposes a server-side carve-out from the zero-knowledge
 * invariant. After enrolment + KEK paste + restart, the server holds the
 * vault key in RAM. See root CLAUDE.md "Server-side backup carve-out" for
 * the full discussion.
 */
export function BackupSettingsModal({ onClose }: { onClose: () => void }) {
  const rawVaultKey = useVault((s) => s.rawVaultKey);
  const canAdminister = useCanAdminister();
  const [status, setStatus] = useState<BackupStatusResponse | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kekOnce, setKekOnce] = useState<string | null>(null);
  const [postRunMessage, setPostRunMessage] = useState<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Defensive admin guard. The Workspace overflow menu hides this entry for
  // non-admins and the server gates every endpoint with adminGate, but
  // short-circuit to null so nothing leaks if reached another way.
  if (!canAdminister) return null;

  async function reload() {
    setError(null);
    try {
      const s = await api.getBackupStatus();
      setStatus(s);
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Could not load backup status.');
    }
  }

  useEffect(() => {
    void reload();
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, []);

  // While a run is in progress, poll status every 5 s so the panel updates
  // without the operator clicking refresh. Stop polling when inProgress flips
  // false.
  useEffect(() => {
    if (status?.inProgress) {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = setInterval(() => {
        void reload();
      }, 5000);
    } else if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    return () => {
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
  }, [status?.inProgress]);

  async function enrol() {
    if (!rawVaultKey) {
      setError('Vault is locked. Unlock first.');
      return;
    }
    setBusy('enrol');
    setError(null);
    try {
      const bundle = await generateKekWrappedKeypair();
      const wrappedVaultKey = await sealToPublicKey(rawVaultKey, bundle.publicKey);
      await api.enrolBackupIdentity({
        publicKey: bundle.publicKey,
        encryptedPrivateKey: bundle.encryptedPrivateKey,
        wrappedVaultKey,
      });
      const kekB64 = toB64(bundle.kek);
      bundle.privateKey.fill(0);
      bundle.kek.fill(0);
      setKekOnce(kekB64);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Enrolment failed.');
    } finally {
      setBusy(null);
    }
  }

  async function revoke() {
    if (
      !confirm(
        'Revoke the backup identity? Scheduled backups will stop on the next server restart. Existing backups in Dropbox are NOT deleted. You can re-enrol later.',
      )
    ) {
      return;
    }
    setBusy('revoke');
    setError(null);
    try {
      await api.revokeBackupIdentity();
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Revoke failed.');
    } finally {
      setBusy(null);
    }
  }

  async function connectDropbox() {
    // Two-step redirect: POST to /backup/dropbox/start with our JWT (so
    // the server can set the state cookie + validate admin auth), then
    // navigate the browser to the returned Dropbox authorize URL. The
    // cookie persists across the round-trip back to /auth/dropbox/callback.
    setBusy('connect');
    setError(null);
    try {
      const res = await api.dropboxStart();
      window.location.href = res.authorizeUrl;
    } catch (err: any) {
      setBusy(null);
      setError(err?.body?.error ?? err?.message ?? 'Could not start Dropbox OAuth.');
    }
  }

  async function disconnectDropbox() {
    if (!confirm('Disconnect from Dropbox? Existing backups in your Dropbox are NOT deleted.')) {
      return;
    }
    setBusy('disconnect');
    setError(null);
    try {
      await api.disconnectDropbox();
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Disconnect failed.');
    } finally {
      setBusy(null);
    }
  }

  async function runNow() {
    setBusy('run');
    setError(null);
    setPostRunMessage(null);
    try {
      const res = await api.runBackupNow();
      setPostRunMessage(`Backup run started (id ${res.runId.slice(0, 8)}). Status will refresh as it progresses.`);
      await reload();
    } catch (err: any) {
      setError(err?.body?.error ?? err?.message ?? 'Run-now failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50">
      <div className="card w-full max-w-2xl max-h-[100dvh] md:max-h-[85vh] flex flex-col">
        <div className="flex items-center gap-2 border-b border-ink-800 px-4 py-3">
          <HardDriveDownload size={18} className="text-dragon-500" />
          <h2 className="font-semibold">Scheduled Dropbox backup</h2>
          <button onClick={onClose} className="btn-ghost ml-auto !p-1" disabled={!!busy}>
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-4 text-sm">
          {!status ? (
            <p className="text-xs text-ink-500">Loading…</p>
          ) : (
            <>
              <ImmutabilityNote />

              {/* State 1: Not enrolled */}
              {!status.enrolled && !kekOnce && (
                <NotEnrolledSection onEnrol={enrol} busy={busy === 'enrol'} />
              )}

              {/* KEK shown once after enrolment */}
              {kekOnce && (
                <KekOnceCard
                  kek={kekOnce}
                  envVarName="DV_BACKUP_KEK"
                  envFilePath="packages/server/.env"
                  systemdUnit="dragonhq"
                  onDismiss={() => setKekOnce(null)}
                />
              )}

              {/* State 2: misconfigured */}
              {status.enrolled && status.keyState === 'disabled_misconfigured' && (
                <MisconfiguredBanner onRevoke={revoke} busy={busy === 'revoke'} />
              )}

              {/* State 3: KEK absent */}
              {status.enrolled && status.keyState === 'disabled_no_kek' && (
                <NoKekBanner />
              )}

              {/* State 4: ready, Dropbox not connected */}
              {status.enrolled && status.keyState === 'ready' && !status.dropboxConnected && (
                <ReadyConnectDropbox onConnect={connectDropbox} />
              )}

              {/* State 5: fully ready */}
              {status.enrolled && status.keyState === 'ready' && status.dropboxConnected && (
                <FullyReady
                  status={status}
                  busy={busy}
                  onRunNow={runNow}
                  onDisconnect={disconnectDropbox}
                  onRevoke={revoke}
                  onRefresh={reload}
                  postRunMessage={postRunMessage}
                />
              )}
            </>
          )}
          {error && (
            <p className="text-xs text-red-400 flex items-start gap-1">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function ImmutabilityNote() {
  return (
    <div className="rounded border border-ink-800 bg-ink-950/50 p-3 text-xs text-ink-400 flex items-start gap-2">
      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-amber-500" />
      <span>
        Scheduled backups are an explicit carve-out from the zero-knowledge invariant. After
        you paste the KEK and restart, the server holds the vault key in RAM so it can
        decrypt every customer/document/attachment on a 3-hour cadence. Backups in Dropbox
        are <strong>not deleted when you delete from Dragon HQ</strong> — restoring from a
        backup may bring back data you removed.
      </span>
    </div>
  );
}

function NotEnrolledSection({ onEnrol, busy }: { onEnrol: () => void; busy: boolean }) {
  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-500">
        Generate a backup identity so the server can run unattended backups. A random
        32-byte KEK will be generated in your browser and shown <strong>once</strong> —
        you must paste it into <code>DV_BACKUP_KEK</code> in{' '}
        <code>packages/server/.env</code> and restart the server before backups can run.
      </p>
      <button className="btn-primary" onClick={onEnrol} disabled={busy}>
        {busy ? 'Setting up…' : 'Set up scheduled backup'}
      </button>
    </div>
  );
}

function MisconfiguredBanner({ onRevoke, busy }: { onRevoke: () => void; busy: boolean }) {
  return (
    <div className="rounded border border-red-900 bg-red-950/30 p-3 text-xs text-red-300 space-y-3">
      <div className="flex items-start gap-2">
        <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
        <span>
          A backup identity is enrolled but <code>DV_BACKUP_KEK</code> in the server env
          file does not unwrap it. Either restore the correct KEK and restart the server,
          or revoke and re-enrol from scratch.
        </span>
      </div>
      <button className="btn-ghost text-red-300 hover:bg-red-950" onClick={onRevoke} disabled={busy}>
        <Trash2 size={12} className="mr-1" /> {busy ? 'Revoking…' : 'Revoke and start over'}
      </button>
    </div>
  );
}

function NoKekBanner() {
  return (
    <div className="rounded border border-amber-900 bg-amber-950/30 p-3 text-xs text-amber-200 flex items-start gap-2">
      <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
      <span>
        A backup identity is enrolled but <code>DV_BACKUP_KEK</code> is empty in the server
        env file. Paste the KEK that was shown at enrolment time into{' '}
        <code>packages/server/.env</code> and run <code>sudo systemctl restart dragonhq</code>.
        If you've lost the KEK, revoke the identity and re-enrol below.
      </span>
    </div>
  );
}

function ReadyConnectDropbox({ onConnect }: { onConnect: () => void }) {
  return (
    <div className="space-y-3">
      <div className="rounded border border-emerald-900 bg-emerald-950/30 p-3 text-xs text-emerald-200 flex items-start gap-2">
        <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />
        <span>Backup identity unlocked. Now connect a Dropbox account to receive the backups.</span>
      </div>
      <button className="btn-primary" onClick={onConnect}>
        <Cloud size={14} className="mr-1" /> Connect Dropbox
      </button>
      <p className="text-xs text-ink-500">
        You'll be redirected to Dropbox to grant access to the app folder. The folder is
        sandboxed to <code>/Apps/Dragon HQ</code> — Dragon HQ never sees the rest of your
        Dropbox.
      </p>
    </div>
  );
}

function FullyReady({
  status,
  busy,
  onRunNow,
  onDisconnect,
  onRevoke,
  onRefresh,
  postRunMessage,
}: {
  status: BackupStatusResponse;
  busy: string | null;
  onRunNow: () => void;
  onDisconnect: () => void;
  onRevoke: () => void;
  onRefresh: () => void;
  postRunMessage: string | null;
}) {
  return (
    <div className="space-y-3">
      <div className="rounded border border-ink-800 p-3 text-xs space-y-1">
        <div className="flex items-center gap-2">
          <CheckCircle2 size={14} className="text-emerald-500" />
          <span>Connected to Dropbox account: </span>
          <span className="font-mono">{status.dropboxAccountEmail ?? '(unknown)'}</span>
        </div>
        <RunRow label="Last run" run={status.lastRun} inProgress={status.inProgress} />
        {status.lastRun?.id !== status.lastSuccess?.id && (
          <RunRow label="Last success" run={status.lastSuccess} />
        )}
      </div>

      {postRunMessage && (
        <p className="text-xs text-emerald-300 flex items-center gap-1">
          <PlayCircle size={12} /> {postRunMessage}
        </p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          className="btn-primary"
          onClick={onRunNow}
          disabled={!!busy || status.inProgress}
          title="Forces past the change-detection check — uploads a fresh backup even if nothing has changed."
        >
          <PlayCircle size={14} className="mr-1" /> {busy === 'run' || status.inProgress ? 'Running…' : 'Run now'}
        </button>
        <button
          className="btn-ghost"
          onClick={onRefresh}
          disabled={!!busy}
          title="Refresh status"
        >
          <RotateCw size={14} className="mr-1" /> Refresh
        </button>
        <button
          className="btn-ghost ml-auto"
          onClick={onDisconnect}
          disabled={!!busy}
          title="Stops scheduled backups from uploading. Existing Dropbox folders are kept."
        >
          <CloudOff size={14} className="mr-1" /> Disconnect Dropbox
        </button>
        <button
          className="btn-ghost text-red-300 hover:bg-red-950"
          onClick={onRevoke}
          disabled={!!busy}
          title="Removes the server-side vault-key custody. Existing Dropbox folders are kept."
        >
          <Trash2 size={14} className="mr-1" /> Revoke identity
        </button>
      </div>
    </div>
  );
}

function RunRow({
  label,
  run,
  inProgress,
}: {
  label: string;
  run: BackupStatusResponse['lastRun'];
  inProgress?: boolean;
}) {
  if (!run) {
    return (
      <div className="text-ink-500">
        <span className="font-medium text-ink-400">{label}:</span> none yet
      </div>
    );
  }
  const when = new Date(run.startedAt).toLocaleString();
  const status =
    inProgress && run.status === 'running'
      ? 'running…'
      : run.status === 'success'
        ? `success (${run.customerCount ?? 0} customers, ${run.documentCount ?? 0} docs, ${run.attachmentCount ?? 0} attachments)`
        : run.status === 'skipped_no_changes'
          ? 'skipped (no changes)'
          : run.status === 'failed'
            ? `failed: ${run.errorMessage ?? '(no message)'}`
            : run.status;
  return (
    <div className="text-ink-300">
      <span className="font-medium text-ink-400">{label}:</span> {when} — {status}
    </div>
  );
}
